import Cookies from 'js-cookie';
import { useRouter } from 'next/navigation';
import { type MouseEvent, useCallback } from 'react';

import type { GameV1 } from '@/api/games';
import { COOKIE_NAME } from '@/helpers/cookie';
import { isGamePage } from '@/helpers/game';
import { Modals } from '@/helpers/modals';
import { usePlayerFieldsStatus } from '@/hooks/usePlayerFieldsStatus';
import { useGetCommonStore, useModalsStore } from '@/store';

export const useGamePlayAction = (
  game?: GameV1,
  onPlayClick?: (game: GameV1) => void
) => {
  const useCommonStore = useGetCommonStore();

  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const { push } = useRouter();
  const open = useModalsStore.use.open();
  const { missingFields } = usePlayerFieldsStatus(game?.provider);

  const handlePlay = useCallback(
    (event: MouseEvent) => {
      if (!game) return;

      onPlayClick && onPlayClick(game);

      if (!isGamePage(window.location.pathname)) {
        useCommonStore.setState({
          gameOpenBackUrl: window.location.href,
        });
      }

      if (isAuthenticated && missingFields.length) {
        event.preventDefault();
        open(Modals.CompleteProfile, {
          customOnClose: () => {
            push(encodeURI(`/game/${game.fullPath}`));
          },
        });
        return;
      }

      if (!isAuthenticated) {
        event.preventDefault();
        const isRegistered = Cookies.get(COOKIE_NAME.IS_REGISTERED) === 'true';

        useCommonStore.setState({
          afterLoginRedirectUrl: encodeURI(`/game/${game.fullPath}`),
        });
        open(isRegistered ? Modals.Login : Modals.Registration);
        return;
      }
    },
    [game, onPlayClick, isAuthenticated, missingFields.length, open, push]
  );

  return handlePlay;
};
